import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Copy,
    Check,
    AlertTriangle,
    Clock,
    Shield,
    Key,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────

function base64UrlDecode(str: string): string {
    const padded = str.replace(/-/g, "+").replace(/_/g, "/");
    const pad = padded.length % 4;
    const final = pad ? padded + "=".repeat(4 - pad) : padded;
    return decodeURIComponent(
        escape(atob(final)),
    );
}

interface DecodedJWT {
    header: Record<string, unknown>;
    payload: Record<string, unknown>;
    signature: string;
    raw: { header: string; payload: string; signature: string };
}

function decodeJWT(token: string): DecodedJWT {
    const parts = token.trim().split(".");
    if (parts.length !== 3) throw new Error("Invalid JWT: must have 3 parts separated by dots");

    const header = JSON.parse(base64UrlDecode(parts[0]!));
    const payload = JSON.parse(base64UrlDecode(parts[1]!));

    return {
        header,
        payload,
        signature: parts[2]!,
        raw: { header: parts[0]!, payload: parts[1]!, signature: parts[2]! },
    };
}

function formatTimestamp(ts: number): string {
    return new Date(ts * 1000).toLocaleString();
}

function isExpired(payload: Record<string, unknown>): boolean {
    if (typeof payload.exp !== "number") return false;
    return Date.now() / 1000 > payload.exp;
}

function timeUntilExpiry(exp: number): string {
    const diff = exp - Date.now() / 1000;
    if (diff < 0) {
        const ago = Math.abs(diff);
        if (ago < 60) return `${Math.round(ago)}s ago`;
        if (ago < 3600) return `${Math.round(ago / 60)}m ago`;
        if (ago < 86400) return `${Math.round(ago / 3600)}h ago`;
        return `${Math.round(ago / 86400)}d ago`;
    }
    if (diff < 60) return `in ${Math.round(diff)}s`;
    if (diff < 3600) return `in ${Math.round(diff / 60)}m`;
    if (diff < 86400) return `in ${Math.round(diff / 3600)}h`;
    return `in ${Math.round(diff / 86400)}d`;
}

// ── Component ────────────────────────────────────────────────

export function JwtDecoder() {
    const [token, setToken] = useState("");
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        document.title =
            "JWT Decoder — Decode & Inspect JSON Web Tokens — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free JWT decoder and inspector. Paste a JSON Web Token to see its header, payload, expiry status, and claims. No data sent to any server.",
            );
        }
    }, []);

    const decoded = useMemo(() => {
        if (!token.trim()) return null;
        try {
            return { data: decodeJWT(token), error: null };
        } catch (e: unknown) {
            return { data: null, error: e instanceof Error ? e.message : "Invalid JWT" };
        }
    }, [token]);

    const copyValue = async (value: string, key: string) => {
        await navigator.clipboard.writeText(value);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    const expired = decoded?.data ? isExpired(decoded.data.payload) : false;

    const CLAIM_LABELS: Record<string, string> = {
        iss: "Issuer",
        sub: "Subject",
        aud: "Audience",
        exp: "Expires",
        nbf: "Not Before",
        iat: "Issued At",
        jti: "JWT ID",
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            {/* Nav */}
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link
                    to="/"
                    className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
                >
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        tools
                    </Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">
                        home
                    </Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        Tools
                    </Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">JWT Decoder</span>
                </div>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
                        JWT Decoder
                    </h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Paste a JSON Web Token to decode its header and payload. See expiry
                        status, registered claims, and algorithm details instantly.
                    </p>
                </div>

                {/* Input */}
                <div className="mb-6">
                    <div className="px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                        <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                            Token
                        </span>
                    </div>
                    <textarea
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        placeholder="Paste your JWT here... (eyJhbGciOiJIUzI1NiIs...)"
                        spellCheck={false}
                        className="w-full h-[120px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 focus:border-white/[0.1] transition-colors"
                    />
                </div>

                {/* Error state */}
                {decoded?.error && (
                    <div className="flex items-center gap-2 text-[13px] text-red-400 mb-6 p-4 bg-red-400/[0.05] border border-red-400/10 rounded-xl">
                        <AlertTriangle size={14} />
                        {decoded.error}
                    </div>
                )}

                {/* Decoded output */}
                {decoded?.data && (
                    <div className="space-y-4 mb-8">
                        {/* Expiry badge */}
                        {typeof decoded.data.payload.exp === "number" && (
                            <div
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[12px] font-medium ${expired
                                    ? "bg-red-400/10 text-red-400 border border-red-400/20"
                                    : "bg-green-400/10 text-green-400 border border-green-400/20"
                                    }`}
                            >
                                <Clock size={12} />
                                {expired ? "Expired" : "Valid"} ·{" "}
                                {timeUntilExpiry(decoded.data.payload.exp as number)}
                            </div>
                        )}

                        {/* Header */}
                        <div>
                            <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                                <div className="flex items-center gap-2">
                                    <Shield size={12} className="text-zinc-600" />
                                    <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                        Header
                                    </span>
                                </div>
                                <button
                                    onClick={() =>
                                        copyValue(
                                            JSON.stringify(decoded.data!.header, null, 2),
                                            "header",
                                        )
                                    }
                                    className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                >
                                    {copied === "header" ? (
                                        <Check size={11} className="text-green-400" />
                                    ) : (
                                        <Copy size={11} />
                                    )}
                                </button>
                            </div>
                            <pre className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono overflow-auto">
                                {JSON.stringify(decoded.data.header, null, 2)}
                            </pre>
                        </div>

                        {/* Payload */}
                        <div>
                            <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                                <div className="flex items-center gap-2">
                                    <Key size={12} className="text-zinc-600" />
                                    <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                        Payload
                                    </span>
                                </div>
                                <button
                                    onClick={() =>
                                        copyValue(
                                            JSON.stringify(decoded.data!.payload, null, 2),
                                            "payload",
                                        )
                                    }
                                    className="text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                >
                                    {copied === "payload" ? (
                                        <Check size={11} className="text-green-400" />
                                    ) : (
                                        <Copy size={11} />
                                    )}
                                </button>
                            </div>
                            <pre className="bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono overflow-auto">
                                {JSON.stringify(decoded.data.payload, null, 2)}
                            </pre>
                        </div>

                        {/* Claims table */}
                        <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-white/[0.04]">
                                <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                    Registered Claims
                                </span>
                            </div>
                            <div className="divide-y divide-white/[0.04]">
                                {Object.entries(decoded.data.payload)
                                    .filter(([k]) => k in CLAIM_LABELS)
                                    .map(([key, value]) => (
                                        <div
                                            key={key}
                                            className="flex items-center justify-between px-4 py-2.5 text-[13px]"
                                        >
                                            <span className="text-zinc-500">
                                                {CLAIM_LABELS[key]} ({key})
                                            </span>
                                            <span className="text-zinc-300 font-mono">
                                                {key === "exp" || key === "iat" || key === "nbf"
                                                    ? formatTimestamp(value as number)
                                                    : String(value)}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* SEO content */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">
                        What is a JSON Web Token?
                    </h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>
                            A JSON Web Token (JWT) is a compact, URL-safe way to represent
                            claims between two parties. JWTs are widely used for
                            authentication and authorization in web APIs. They consist of
                            three parts: a header (algorithm and type), a payload (claims and
                            data), and a signature.
                        </p>
                        <p>
                            This decoder splits the token into its parts and displays the
                            decoded header and payload as formatted JSON. It also highlights
                            whether the token has expired and shows registered claims like
                            issuer, subject, and expiration time.
                        </p>
                        <p>
                            Everything runs entirely in your browser — no data is ever sent
                            to a server. Built by the team behind{" "}
                            <Link
                                to="/"
                                className="text-violet-400 hover:text-violet-300 transition-colors"
                            >
                                Ballah Code
                            </Link>
                            .
                        </p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/jwt-encoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Encoder</Link>
                        <Link to="/tools/base64" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Base64 Encode / Decode</Link>
                        <Link to="/tools/unix-timestamp" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Unix Timestamp Converter</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">
                    ballah code
                </Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">
                    tools
                </Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
